import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CheckCircle2, ShieldCheck, Loader2, RefreshCw,
  Inbox, ListTodo, TrendingUp, Users, CreditCard, Eye, MousePointerClick,
  Sparkles, ArrowRight, Plus, X, Check,
} from 'lucide-react';
import AdminPwaInstallCard from '@/components/admin/AdminPwaInstallCard';
import OwnerActionCenterBlock from '@/components/admin/OwnerActionCenterBlock';
import PilotHeroCard, { OwnerKpiStrip } from '@/components/admin/PilotHeroCard';
import PaymentDiagnosticCard from '@/components/admin/PaymentDiagnosticCard';
import AgentStatusCompact from '@/components/admin/AgentStatusCompact';
import Blok90ReconciliationCard from '@/components/admin/Blok90ReconciliationCard';

const API = '/api';

const SEVERITY_STYLE = {
  ok: { bg: 'bg-emerald-500/10 border-emerald-400/30', text: 'text-emerald-200', dot: 'bg-emerald-400', label: 'OK' },
  warning: { bg: 'bg-amber-500/10 border-amber-400/30', text: 'text-amber-200', dot: 'bg-amber-400', label: 'Let op' },
  blocked: { bg: 'bg-rose-500/10 border-rose-400/30', text: 'text-rose-200', dot: 'bg-rose-400', label: 'Geblokkeerd' },
};

const KPI_LABELS = {
  page_view: { label: 'Bezoeken', icon: Eye },
  quickscan_started: { label: 'Quickscans', icon: MousePointerClick },
  quickscan_completed: { label: 'Voltooid', icon: Activity },
  demo_viewed: { label: 'Demo views', icon: Sparkles },
  demo_cta_clicked: { label: 'Demo CTA', icon: TrendingUp },
  checkout_started: { label: 'Checkout', icon: CreditCard },
  payment_completed: { label: 'Betaald', icon: CheckCircle2 },
};

function KpiCard({ k, today, last_7d }) {
  const meta = KPI_LABELS[k] || { label: k, icon: Activity };
  const Icon = meta.icon;
  return (
    <div className="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-3 sm:p-4 hover:border-emerald-400/30 transition-colors" data-testid={`cc-kpi-${k}`}>
      <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-wider text-white/55">
        <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        <span>{meta.label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-xl sm:text-2xl font-medium text-white" style={{ fontFamily: 'Outfit' }} data-testid={`cc-kpi-${k}-today`}>
          {today ?? 0}
        </span>
        <span className="text-[10px] sm:text-xs text-white/50">vandaag</span>
      </div>
      <div className="text-[10px] sm:text-xs text-white/50 mt-0.5" data-testid={`cc-kpi-${k}-7d`}>
        {last_7d ?? 0} laatste 7d
      </div>
    </div>
  );
}

function BlockerCard({ blocker }) {
  const st = SEVERITY_STYLE[blocker.severity] || SEVERITY_STYLE.warning;
  const Icon = blocker.severity === 'ok' ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`rounded-lg border p-3 ${st.bg}`} data-testid={`cc-blocker-${blocker.id}`}>
      <div className="flex items-start gap-2">
        <span className={`w-2 h-2 rounded-full mt-1.5 ${st.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Icon className={`w-3.5 h-3.5 ${st.text}`} />
            <span className={`text-xs font-semibold ${st.text}`}>{blocker.title}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.text} bg-white/5 border border-white/15`}>{st.label}</span>
          </div>
          <p className={`text-[11px] sm:text-xs mt-1 leading-relaxed ${st.text} opacity-90`}>{blocker.description}</p>
          {blocker.external_action_required && (
            <p className="text-[10px] text-white/50 mt-1.5 italic">
              Aparte GO vereist · geen automatische actie
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function OpenActionsCard({ openActions, onNavigateToInbox }) {
  if (!openActions) return null;
  const types = Object.entries(openActions.by_type || {});
  return (
    <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 sm:p-5 shadow-2xl" data-testid="cc-open-actions">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <Inbox className="w-4 h-4" />
          </span>
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Approval Inbox</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 font-medium" data-testid="cc-open-actions-total">
            {openActions.total} open
          </span>
        </div>
        <button
          onClick={onNavigateToInbox}
          className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1 transition-colors"
          data-testid="cc-open-inbox-btn"
        >
          Open inbox <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {types.map(([t, count]) => (
          <button
            key={t}
            onClick={onNavigateToInbox}
            className="text-left bg-white/5 hover:bg-white/10 rounded-lg p-2.5 border border-white/10 hover:border-emerald-400/30 transition-colors"
            data-testid={`cc-open-actions-${t}`}
          >
            <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-white/55 truncate">
              {openActions.labels?.[t] || t}
            </div>
            <div className="text-lg sm:text-xl font-medium text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>
              {count}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TodosWidget({ token, onRefresh }) {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/control-center/todos?status=open&limit=10`, { headers: authH });
      setTodos(data.todos || []);
    } catch { /* silent */ }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => {
    try {
      await axios.patch(`${API}/admin/control-center/todos/${id}`, { status: 'done' }, { headers: authH });
      toast.success('Afgevinkt');
      load();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kon to-do niet bijwerken');
    }
  };

  const createTodo = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await axios.post(`${API}/admin/control-center/todos`, { title: newTitle.trim(), priority: 'normal' }, { headers: authH });
      setNewTitle('');
      toast.success('To-do toegevoegd');
      load();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kon to-do niet aanmaken');
    }
  };

  return (
    <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 sm:p-5 shadow-2xl" data-testid="cc-todos">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <ListTodo className="w-4 h-4" />
          </span>
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Mijn to-do&apos;s</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/8 text-white/65 border border-white/15" data-testid="cc-todos-count">
            {todos.length} open
          </span>
        </div>
        <button onClick={load} disabled={loading} className="text-white/55 hover:text-emerald-300 transition-colors" data-testid="cc-todos-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      <form onSubmit={createTodo} className="flex gap-2 mb-3">
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Snel een nieuwe to-do toevoegen"
          className="flex-1 text-xs sm:text-sm bg-white/5 border border-white/15 rounded-md px-2.5 py-1.5 text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/50"
          data-testid="cc-todo-new-input"
        />
        <button
          type="submit"
          disabled={!newTitle.trim()}
          className="px-2.5 py-1.5 text-xs sm:text-sm bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold disabled:opacity-50 flex items-center gap-1 transition-colors"
          data-testid="cc-todo-new-submit"
        >
          <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Voeg toe</span>
        </button>
      </form>

      {todos.length === 0 ? (
        <p className="text-xs text-white/50 text-center py-4" data-testid="cc-todos-empty">
          Geen openstaande to-do&apos;s. Approve een item in de inbox of voeg er hier zelf één toe.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="cc-todos-list">
          {todos.map((t) => (
            <li key={t.todo_id} className="flex items-start gap-2 text-xs sm:text-sm bg-white/5 border border-white/10 rounded-md px-2.5 py-2" data-testid={`cc-todo-${t.todo_id}`}>
              <button
                onClick={() => markDone(t.todo_id)}
                className="mt-0.5 w-4 h-4 rounded border border-white/25 hover:bg-emerald-500 hover:border-emerald-400 hover:text-emerald-950 flex items-center justify-center transition-colors"
                data-testid={`cc-todo-done-${t.todo_id}`}
                title="Afvinken"
              >
                <Check className="w-3 h-3 opacity-0 hover:opacity-100" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-medium leading-tight text-white">{t.title}</div>
                {t.detail && <div className="text-[10px] sm:text-[11px] text-white/55 mt-0.5 line-clamp-2">{t.detail}</div>}
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {t.priority === 'high' && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/30 font-medium">High</span>
                  )}
                  {t.auto_generated && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-400/30">Auto</span>
                  )}
                  {t.related_type && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-white/8 text-white/65 border border-white/15">{t.related_type}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ControlCenterTab({ token, onNavigateToInbox, onNavigateToTab }) {
  const [summary, setSummary] = useState(null);
  const [blockers, setBlockers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        axios.get(`${API}/admin/control-center/summary`, { headers: authH }),
        axios.get(`${API}/admin/control-center/blockers`, { headers: authH }),
      ]);
      setSummary(s.data);
      setBlockers(b.data);
    } catch (e) {
      toast.error('Kon Control Center niet laden');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshKey]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="cc-loading">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-12" data-testid="cc-error">
        <p className="text-sm text-muted-foreground">Geen data beschikbaar.</p>
      </div>
    );
  }

  const kpiKeys = ['page_view', 'quickscan_started', 'quickscan_completed', 'demo_viewed', 'demo_cta_clicked', 'checkout_started', 'payment_completed'];

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="control-center-tab">
      {/* Blok 90 — Stripe ↔ Mongo Reconciliation (Feb 2026, prominent voor diagnose) */}
      <Blok90ReconciliationCard token={token} />
      {/* Owner Action Center — beslis-laag (Feb 2026) */}
      <OwnerActionCenterBlock token={token} />

      {/* Pilot Hero — most important owner action */}
      <PilotHeroCard
        token={token}
        onOpenPilotAccess={() => onNavigateToTab && onNavigateToTab('pilot_devices')}
        onOpenInbox={onNavigateToInbox}
      />

      {/* Owner KPI strip — leads / offertes / betalingen / safety */}
      <OwnerKpiStrip summary={summary} />

      {/* Payment health diagnostic (read-only, no Stripe mutation) */}
      <PaymentDiagnosticCard token={token} />

      {/* Header strip — refresh + sub-title */}
      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
        <div>
          <h2 className="text-base sm:text-lg font-semibold tracking-tight flex items-center gap-2 text-white" style={{ fontFamily: 'Outfit' }}>
            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-300" />
            Control Center
          </h2>
          <p className="text-[11px] sm:text-xs text-white/55 mt-0.5">
            Owner dashboard &middot; {summary.platform?.active_subscribers ?? 0} actieve abonnees &middot; {summary.platform?.paid_payments_24h ?? 0} betalingen laatste 24u
          </p>
        </div>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
          className="px-2.5 py-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/15 rounded-md text-white/85 flex items-center gap-1.5 transition-colors"
          data-testid="cc-refresh-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Open Actions + Todos (mobile-first stacked) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OpenActionsCard openActions={summary.open_actions} onNavigateToInbox={onNavigateToInbox} />
        <TodosWidget token={token} onRefresh={() => setRefreshKey((k) => k + 1)} />
      </div>

      {/* Funnel KPIs */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-white/55">Funnel &mdash; vandaag</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2 sm:gap-3" data-testid="cc-kpis-grid">
          {kpiKeys.map((k) => (
            <KpiCard
              key={k}
              k={k}
              today={summary.kpis.today[k]}
              last_7d={summary.kpis.last_7d[k]}
            />
          ))}
        </div>
      </div>

      {/* Systeemstatus — technical integrations & blockers */}
      <div data-testid="cc-systemstatus">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-white/55">
            Systeemstatus
          </h3>
          {blockers && (
            <div className="flex items-center gap-1.5 text-[10px]" data-testid="cc-blockers-summary">
              {blockers.summary.blocked > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/30 font-medium">{blockers.summary.blocked} blocked</span>
              )}
              {blockers.summary.warning > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30 font-medium">{blockers.summary.warning} warning</span>
              )}
              {blockers.summary.ok > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 font-medium">{blockers.summary.ok} OK</span>
              )}
            </div>
          )}
        </div>
        {blockers && blockers.blockers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="cc-blockers-list">
            {blockers.blockers.map((b) => <BlockerCard key={b.id} blocker={b} />)}
          </div>
        ) : (
          <p className="text-xs text-white/55">Geen integratiestatus geladen.</p>
        )}
      </div>

      {/* Agentstatus compact — approval-first labels per agent */}
      <AgentStatusCompact />

      {/* Safety note */}
      <div className="bg-sky-500/10 border border-sky-400/30 rounded-lg px-3 py-2 text-[11px] sm:text-xs text-sky-100" data-testid="cc-safety-note">
        <strong className="text-white">Veiligheid:</strong> Approve-acties in de inbox updaten alleen interne status + maken een to-do voor jou aan.
        G&eacute;&eacute;n externe calls (geen Daisycon publish, geen Google Ads launch, geen Resend mail, geen Stripe charge, geen provider mail, geen device control).
      </div>

      {/* PWA install card — admin only, auto-hidden in standalone mode */}
      <AdminPwaInstallCard />
    </div>
  );
}
